import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["query"];
import PortalIdParser from 'PortalIdParser';
import { stringify } from 'hub-http/helpers/params';
import { buildUrl } from 'hub-http/helpers/url';
import { getFullUrl, getFullUrlWithCurrentSubDomain } from 'hubspot-url-utils';
import { SELECTED_BUSINESS_UNIT } from '../constants/LocalStorageKeys';
import { pipe } from './functionsCompositionUtils';
import { DEFAULT_BUSINESS_UNIT_ID } from '../constants/businessUnits';

// local.hubspotqa.com ->  local.hubspotqa.com/api
// app-test2.hubspotqa.com -> app-test2.hubspotqa.com/api
// app.hubspot.com -> app.hubspot.com/api
// app.hubspotqa.com -> app.hubspotqa.com/api
export function buildAbsoluteAPIUrl(_ref) {
  let {
      query
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return buildUrl(Object.assign({
    hostname: getFullUrl('app-api').replace(/^https?:\/\//, ''),
    query: stringify(query)
  }, rest));
}
const ACCOUNT_ID_TEMPLATE_RE = /\${(?:ACCOUNT_ID|accountId|account-id)}/i;
const APP_ID_TEMPLATE_RE = /\${(?:APP_ID|appId|app-id)}/i;
const PORTAL_ID_TEMPLATE_RE = /\${(?:PORTAL_ID|portalId|portal-id)}/i;
const BUSINESS_UNIT_ID_TEMPLATE_RE = /\${(?:BUSINESS_UNIT_ID|businessUnitId|business-unit-id)}/i;

/**
 * For case like `some-path/account/:accountId[/otherPathParams][?otherSearchParams=value]`
 * where `:accountId` is numbers
 */
const ACCOUNT_ID_PATH_PARAM_RE = /.*?\/account\/(\d+)(?:\/|\?|$)/i;

/**
 * For cases like `some-path?accountId=123456` or `some-path?searchParam1=foo&accountId=123456`
 * where the value of `accountId` is numbers and `accountId` might be the first or the next param in the `location.search`
 */
const ACCOUNT_ID_SEARCH_PARAM_RE = /\?.*accountId=([^&]+)/i;

/**
 * Replaces sub-string with portal ID template in a URL template string with portal ID
 */
export const getUrlWithPortalId = urlTemplate => {
  const portalId = PortalIdParser.get();
  return (urlTemplate === null || urlTemplate === void 0 ? void 0 : urlTemplate.replace(PORTAL_ID_TEMPLATE_RE, `${portalId}`)) || urlTemplate;
};

/**
 * Returns the value of account ID from path or search params
 * Returns null if an account ID has not been found
 */
export const getAccountIdFromUrl = url => {
  const accountIdPathParamMatched = (url || '').match(ACCOUNT_ID_PATH_PARAM_RE);
  if (accountIdPathParamMatched !== null) {
    return parseInt(accountIdPathParamMatched[1], 10);
  }
  const accountIdSearchParamMatched = (url || '').match(ACCOUNT_ID_SEARCH_PARAM_RE);
  return accountIdSearchParamMatched ? decodeURIComponent(accountIdSearchParamMatched[1]) : null;
};

/**
 * This utility function is used for populating of URL with selected business unit ID
 * It is placed near the `populateWithBuId`
 */
export const getSelectedBusinessUnitId = (defaultValue = DEFAULT_BUSINESS_UNIT_ID) => {
  let selectedBuId = defaultValue;
  try {
    const portalId = PortalIdParser.get();
    const parsed = JSON.parse(localStorage.getItem(SELECTED_BUSINESS_UNIT));
    const parsedBusinessUnitId = parsed[portalId];
    selectedBuId = typeof parsedBusinessUnitId === 'number' && !!parsedBusinessUnitId ? parsedBusinessUnitId : defaultValue;
  } catch (err) {
    /** Nothing to do */
  }
  return selectedBuId;
};

/**
 * Returns selected association BU info: TAppAssociationBusinessUnitInfo or null
 * in case when:
 * - `selectedBusinessUnitId` is not provided
 * - if it doesn't match with any business unit's ID
 * - the business units' list is empty
 */
export const getSelectedBusinessUnit = ({
  selectedBusinessUnitId,
  businessUnits
}) => {
  return businessUnits.find(({
    businessUnitId
  }) => businessUnitId === selectedBusinessUnitId) || null;
};

/**
 * It works for URLs like 'some/path/${businessUnitId}'
 * The selected BU ID is collected from the local storage
 */
export const getUrlWithBuId = urlTemplate => {
  const businessUnitId = getSelectedBusinessUnitId();
  let populatedUrl = urlTemplate;
  try {
    populatedUrl = urlTemplate === null || urlTemplate === void 0 ? void 0 : urlTemplate.replace(BUSINESS_UNIT_ID_TEMPLATE_RE, `${businessUnitId}`);
  } catch (err) {
    /** Nothing to do */
  }
  return populatedUrl || urlTemplate;
};

/**
 * Creates a function replaces sub-string with app ID template in a URL template string with given app ID
 */
export const createGetUrlWithAppId = appId => urlTemplate => {
  return appId && urlTemplate ? urlTemplate.replace(APP_ID_TEMPLATE_RE, `${appId}`) : urlTemplate;
};

/**
 * Creates a function replaces sub-string with account ID template in a URL template string with given account ID
 */
export const createGetUrlWithAccountId = accountId => urlTemplate => {
  return accountId && urlTemplate ? urlTemplate.replace(ACCOUNT_ID_TEMPLATE_RE, `${accountId}`) : urlTemplate;
};

/**
 * Returns absolute URL for reconnect button:
 * - for OAuthor apps - to HubSpot OAuthor api
 * - for DS apps - to /integrations-beta/${portalId}/ds-reconnect?appId=${appId}&accountId=${accountId}
 */
export const getAbsoluteReconnectUrl = ({
  urlTemplate,
  appId,
  accountId
}) => {
  if (!urlTemplate) return '';
  const url = pipe(getUrlWithPortalId, createGetUrlWithAppId(appId), createGetUrlWithAccountId(Number(accountId)))(urlTemplate || '');
  if (url.startsWith('https')) return url;
  const isIntegrationsBetaUrl = url.startsWith('/integrations-beta');

  /**
   * There are two different types of absolute URLs:
   * - for navigation to reconnection page (path starts with `/integrations-beta`, has the same subdomain as an opener)
   * - for calls to HS OAuthor (subdomain includes `app`, path has a segment `oauthor`)
   */
  return isIntegrationsBetaUrl ? `${getFullUrlWithCurrentSubDomain()}${url}` : buildAbsoluteAPIUrl({
    path: url
  });
};